import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, GripVertical, Trash2 } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';

import type { Row as RowType } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import {
  FreeformColumnSurface,
  FreeformRow,
  RowSharedHeightResizeBar,
  rowHeightPx,
} from './FreeformRow';

interface Props {
  row: RowType;
  slideId: string;
}

export function Row({ row, slideId }: Props) {
  const sortable = useSortable({
    id: row.id,
    data: { source: 'canvas', kind: 'row', slideId, rowId: row.id },
  });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const isSelected = useEditorStore(
    (s) => s.selection?.kind === 'row' && s.selection.rowId === row.id,
  );
  const actions = useEditorActions();

  // Every column is a freeform canvas now. Single-column rows render one
  // surface (with its own resize handle); multi-column rows render one surface
  // per column side by side, sharing a single height (resized via the bar).
  const multiColumn = row.columns.length > 1;
  const [liveRowHeightPx, setLiveRowHeightPx] = useState<number | null>(null);
  const sharedHeightPx = liveRowHeightPx ?? rowHeightPx(row);

  // Column resize: dragging the divider between two columns rebalances their
  // widthPercent (the pair's total is preserved). Live widths render during
  // the drag; the result persists as two updateColumn ops on release.
  const colsRef = useRef<HTMLDivElement>(null);
  const [liveWidths, setLiveWidths] = useState<Record<string, number> | null>(null);
  const MIN_COL_PCT = 12;
  const startColResize = (e: React.PointerEvent, i: number) => {
    e.preventDefault();
    e.stopPropagation();
    const container = colsRef.current;
    if (!container) return;
    const totalPx = container.getBoundingClientRect().width;
    if (totalPx <= 0) return;
    const left = row.columns[i];
    const right = row.columns[i + 1];
    const startX = e.clientX;
    const l0 = left.widthPercent;
    const pair = l0 + right.widthPercent;
    const clamp = (l: number) => Math.max(MIN_COL_PCT, Math.min(pair - MIN_COL_PCT, l));
    const widthsAt = (clientX: number) => {
      const l = clamp(Math.round(l0 + ((clientX - startX) / totalPx) * 100));
      return { l, r: pair - l };
    };
    const onMove = (ev: PointerEvent) => {
      const { l, r } = widthsAt(ev.clientX);
      setLiveWidths({ [left.id]: l, [right.id]: r });
    };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setLiveWidths(null);
      const { l, r } = widthsAt(ev.clientX);
      if (l !== l0) {
        actions.updateColumn(slideId, row.id, left.id, { widthPercent: l });
        actions.updateColumn(slideId, row.id, right.id, { widthPercent: r });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const widthOf = (c: RowType['columns'][number]) => liveWidths?.[c.id] ?? c.widthPercent;

  const onSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    useEditorStore.getState().setSelection({ kind: 'row', slideId, rowId: row.id });
  };

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    minHeight: 80,
    paddingTop: row.padding.top,
    paddingRight: row.padding.right,
    paddingBottom: row.padding.bottom,
    paddingLeft: row.padding.left,
  };

  return (
    <div
      ref={setNodeRef}
      onClick={onSelect}
      style={style}
      className={cn(
        'group/row relative flex w-full cursor-default rounded-md transition',
        isSelected && 'ring-1 ring-accent/50',
        isDragging && 'opacity-50',
      )}
    >
      {/* drag handle — sits INSIDE the slide edge so it isn't clipped by the
          slide's overflow:hidden container. */}
      <div
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="absolute left-1 top-1/2 z-20 grid h-7 w-7 -translate-y-1/2 cursor-grab place-items-center rounded-md border border-border bg-white text-fg-subtle opacity-0 shadow-pill transition group-hover/row:opacity-100 active:cursor-grabbing"
        title="Drag to reorder"
      >
        <GripVertical size={14} />
      </div>

      {/* row actions */}
      <div className="absolute right-1 top-1 z-20 flex flex-col gap-1 opacity-0 transition group-hover/row:opacity-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            useEditorStore.getState().setSelection({ kind: 'row', slideId, rowId: row.id });
            actions.duplicateSelection();
          }}
          className="grid h-6 w-6 place-items-center rounded-md border border-border bg-white text-fg-subtle shadow-pill hover:text-fg"
          title="Duplicate row"
        >
          <Copy size={11} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            actions.removeRow(slideId, row.id);
          }}
          className="grid h-6 w-6 place-items-center rounded-md border border-border bg-white text-fg-subtle shadow-pill hover:text-red-500"
          title="Delete row"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {!multiColumn ? (
        <FreeformRow row={row} slideId={slideId} />
      ) : (
        <div className="flex min-w-0 flex-1 flex-col">
          <div ref={colsRef} className="flex w-full min-w-0 items-stretch">
            {row.columns.map((c, i) => (
              <Fragment key={c.id}>
                <div className="min-w-0" style={{ width: `${widthOf(c)}%` }}>
                  <FreeformColumnSurface
                    slideId={slideId}
                    row={row}
                    column={c}
                    forcedHeightPx={sharedHeightPx}
                  />
                </div>
                {i < row.columns.length - 1 && (
                  <div
                    onPointerDown={(e) => startColResize(e, i)}
                    onClick={(e) => e.stopPropagation()}
                    className="group/coldiv flex w-3 flex-none cursor-col-resize items-stretch justify-center"
                    title="Drag to resize columns"
                  >
                    <div className="w-0.5 rounded-full bg-transparent transition group-hover/coldiv:bg-accent/60" />
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          <RowSharedHeightResizeBar
            slideId={slideId}
            row={row}
            heightPx={sharedHeightPx}
            onLiveHeight={setLiveRowHeightPx}
          />
        </div>
      )}
    </div>
  );
}
